import React, { useEffect, useRef, useState } from "react";
import {
  PencilIcon,
  CheckIcon,
  ArrowPathIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const NOTE_MAX_CHARS = 100;

/**
 * NoteRow – Click-to-save note with cancel & saving state
 * @param {Object} props
 * @param {string} props.note - Current note from server
 * @param {string} props.noteType - "top" or "bottom"
 * @param {Function} props.onSave - (type, text) => Promise
 * @param {string} props.placeholder
 * @param {string} props.colorClass
 * @param {string} props.counterClass
 */
const NoteRow = ({
  note: serverNote = "",
  noteType,
  onSave,
  placeholder,
  colorClass,
  counterClass,
}) => {
  const [localNote, setLocalNote] = useState(serverNote);
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // Sync local state when server note changes (e.g. after successful save)
  useEffect(() => {
    setLocalNote(serverNote);
  }, [serverNote]);

  // Auto-focus input when editing starts
  useEffect(() => {
    if (editing && inputRef.current && !isSaving) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing, isSaving]);

  const startEditing = () => setEditing(true);

  const cancelEditing = () => {
    setLocalNote(serverNote);
    setEditing(false);
  };

  const saveNote = async () => {
    const trimmed = localNote.trim();
    if (trimmed === serverNote.trim()) {
      setEditing(false);
      return;
    }
    if (isSaving) return;

    setIsSaving(true);
    try {
      await onSave(noteType, trimmed);
      setEditing(false);
    } catch (err) {
      console.error("Note save failed:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const displayText = localNote || "";
  const showPlaceholder = !displayText && !editing;

  return (
    <div className="relative flex items-center gap-2">
      <div className="flex gap-1">
        {editing ? (
          <>
            {/* Cancel */}
            <button
              onClick={cancelEditing}
              disabled={isSaving}
              className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 disabled:opacity-50 transition"
              aria-label="Cancel edit"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>

            {/* Save */}
            <button
              onClick={saveNote}
              disabled={isSaving || localNote.trim() === serverNote.trim()}
              className={`p-1.5 rounded-full transition-all duration-200 
            ${
              isSaving
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-green-100 hover:bg-green-200 text-green-700"
            }`}
              aria-label={isSaving ? "Saving..." : "Save note"}
            >
              {isSaving ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <CheckIcon className="h-4 w-4" />
              )}
            </button>
          </>
        ) : (
          <button
            onClick={startEditing}
            className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition"
            aria-label="Edit note"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {editing ? (
        <>
          <input
            ref={inputRef}
            type="text"
            value={localNote}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= NOTE_MAX_CHARS) setLocalNote(val);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveNote();
              }
              if (e.key === "Escape") {
                cancelEditing();
              }
            }}
            placeholder={placeholder}
            disabled={isSaving}
            className={`note-single-line flex-1 pr-20 border-b-2 border-dashed border-gray-400 focus:border-${
              counterClass.split("-")[0]
            }-500 focus:outline-none transition-colors disabled:opacity-60 ${colorClass} placeholder-gray-400`}
            maxLength={NOTE_MAX_CHARS}
          />
          <span
            className={`absolute right-0 top-1/2 -translate-y-1/2 text-xs font-medium ${counterClass}`}
          >
            {localNote.length}/{NOTE_MAX_CHARS}
          </span>
        </>
      ) : (
        <>
          <span className={`flex-1 truncate ${colorClass}`}>
            {showPlaceholder ? (
              <span className="italic opacity-60">{placeholder}</span>
            ) : (
              displayText
            )}
          </span>
          <span className={`text-xs font-medium ${counterClass}`}>
            {displayText.length}/{NOTE_MAX_CHARS}
          </span>
        </>
      )}
    </div>
  );
};

export default NoteRow;
